import { Controller } from 'stimulus';

// Generic substring filtering for a list of items.
// Wire an input field to the `filter` target and each filterable item to a
// `filterable` target. As the filter input changes, only items whose text content
// include the typed substring stay visible; the rest are hidden.
//
// Optionally, mark group-header elements as `group` targets. A group header is a
// sibling that precedes the filterable items it heads; it's hidden whenever none of
// the filterable siblings following it (up to the next group) remain visible.
export default class extends Controller {
  static targets = ['filter', 'filterable', 'group'];
  static values = { autofocus: { type: Boolean, default: true } };

  #filter = () => {
    const query = this.#query;
    const filterableTargets = this.filterableTargets;
    filterableTargets.forEach((filterable) => {
      filterable.classList.toggle('hidden', !this.#matches(filterable, query));
    });
    this.#filterGroups(filterableTargets);
  };

  #filterGroups(filterableTargets) {
    const filterableSet = new Set(filterableTargets);
    const groupTargets = this.groupTargets;
    const groupSet = new Set(groupTargets);

    groupTargets.forEach((group) => {
      const anyVisible = this.#filterablesForGroup(group, filterableSet, groupSet).some(
        (filterable) => !filterable.classList.contains('hidden')
      );
      group.classList.toggle('hidden', !anyVisible);
    });
  }

  #filterablesForGroup(group, filterableSet, groupSet) {
    const filterables = [];
    for (
      let node = group.nextElementSibling;
      node && !groupSet.has(node);
      node = node.nextElementSibling
    ) {
      if (filterableSet.has(node)) {
        filterables.push(node);
      }
    }
    return filterables;
  }

  #selectFirst = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.#firstVisibleOption?.click();
    }
  };

  #focusWhenShown = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        this.filterTarget.focus();
      }
    });
  });

  clear() {
    this.#clearFiltering();
    this.filterTarget.focus();
  }

  connect() {
    this.filterTarget.addEventListener('input', this.#filter);
    this.filterTarget.addEventListener('keydown', this.#selectFirst);
    if (this.autofocusValue) {
      this.#focusWhenShown.observe(this.filterTarget);
    }
  }

  disconnect() {
    this.filterTarget.removeEventListener('input', this.#filter);
    this.filterTarget.removeEventListener('keydown', this.#selectFirst);
    this.#focusWhenShown.disconnect();
  }

  get #query() {
    return this.filterTarget.value.trim().toLowerCase();
  }

  get #firstVisibleOption() {
    return this.filterableTargets.find((el) => !el.classList.contains('hidden'));
  }

  #matches(candidate, query) {
    const content = candidate.textContent.trim().toLowerCase();
    return !query || content.includes(query);
  }

  #clearFiltering() {
    this.filterTarget.value = '';
    this.#filter();
  }
}
